/**
 * Map Editor Studio v3 - Arena Overlay Authoring Tool
 * Elvis Aloha Collection
 * 
 * OWNER-ONLY TOOL with full editing lifecycle:
 * - Draw rectangles, polygons, seat boxes, markers
 * - Select, move, resize objects
 * - Edit polygon vertices
 * - Undo/Redo with chart-scoped history
 * - Publication workflow: draft → owner_review → approved → published
 * - Audit logging for all changes
 * 
 * COORDINATE SYSTEM:
 * - All geometry uses native image pixel coordinates
 * - Transforms maintain alignment through zoom/pan
 * 
 * REFACTORED ARCHITECTURE:
 * - useMapCoordinates: Screen ↔ image coordinate conversion
 * - useOverlaySelection: Selection, drag, resize, vertex editing
 * - useOverlayHistory: Undo/redo stack per chart
 * - useOverlayPersistence: Server sync
 * - usePublicationWorkflow: Status validation
 */

import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import OpenSeadragon from 'openseadragon';

// Hooks
import { 
  useMapCoordinates, 
  useOverlaySelection, 
  useOverlayHistory,
  useOverlayPersistence,
  usePublicationWorkflow,
  useSeatInventory,
  useSeatDrafts,
  HISTORY_ACTIONS
} from '../hooks';

// Components
import {
  MapDrawingToolbar,
  OverlayPropertiesPanel,
  OverlayLayer,
  OverlaysList
} from './map-editor';
import { 
  useSeatTemplates, 
  usePlacedSeats,
  SeatTemplateSelector,
  RotationControl,
  CloneControls,
  RowGenerator,
  AdvancedGeometryPanel,
  SeatPropertiesPanel
} from './SeatTemplateTools';
import PlacedSeatsLayer, { GhostPreviewCursor } from './PlacedSeatsLayer';
import AisleZoneLayer from './AisleZoneLayer';
import DraftSeatOverlay, { getDraftHandles } from './DraftSeatOverlay';
import DraftSeatPropertiesPanel from './DraftSeatPropertiesPanel';
import SeatGeometryPropertiesPanel from './SeatGeometryPropertiesPanel';
import SeatInventoryLayer, { SeatAssignmentModal, SeatInventoryStats } from './SeatInventoryLayer';
import AssignmentManagerModal from './AssignmentManagerModal';
import RowGeneratorPanel from './RowGeneratorPanel';

// Constants
import { 
  TOOLS, 
  CHART_IDS, 
  CHARTS, 
  PUBLICATION_STATUS,
  generateTempName,
  getToolForcedCategory
} from '../constants/mapEditor';

// Styles
import '../styles/map-editor.css';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const MapEditorStudio = () => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const isMountedRef = useRef(true); // P0 FIX: Track component mount state
  
  // Chart state
  const [activeChartId, setActiveChartId] = useState(CHART_IDS.CONCERT_STAGE);
  const activeChart = useMemo(() => CHARTS[activeChartId], [activeChartId]);
  
  // Viewer state
  const [isReady, setIsReady] = useState(false);
  const [currentZoom, setCurrentZoom] = useState(1);
  // Bumped only when the OSD viewer + its tiled image are confirmed open
  // (initial load AND every chart switch). This is the REACTIVE signal
  // useMapCoordinates needs to (re)attach its sync handlers - mutating
  // viewerRef.current alone never triggers a React effect re-run.
  const [viewerSyncToken, setViewerSyncToken] = useState(0);
  
  // Tool state
  const [activeTool, setActiveTool] = useState(TOOLS.SELECT);
  const [activeCategory, setActiveCategory] = useState('section');
  
  // Overlay state
  const [overlays, setOverlays] = useState([]);
  // Tracks overlay IDs that exist server-side (loaded from server, or
  // successfully saved this session) so deletion knows whether it needs to
  // call the DELETE API or can just drop the object from local state.
  const persistedOverlayIdsRef = useRef(new Set());
  const [currentDrawing, setCurrentDrawing] = useState(null);
  const [mouseImageCoords, setMouseImageCoords] = useState({ x: 0, y: 0 });
  
  // UI state
  const [saveMessage, setSaveMessage] = useState('');
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  const [overlaysPanelCollapsed, setOverlaysPanelCollapsed] = useState(false);
  const [seatInventoryPanelCollapsed, setSeatInventoryPanelCollapsed] = useState(true);
  
  // Seat Template state
  const [activeSeatRotation, setActiveSeatRotation] = useState(0);
  const [pendingSeatSection, setPendingSeatSection] = useState('BB');
  const [pendingSeatRow, setPendingSeatRow] = useState('1');
  const [pendingSeatNumber, setPendingSeatNumber] = useState('14');
  
  // Refs for seat placement state (to avoid stale closures in handlers)
  const activeSeatRotationRef = useRef(activeSeatRotation);
  const pendingSeatSectionRef = useRef(pendingSeatSection);
  const pendingSeatRowRef = useRef(pendingSeatRow);
  const pendingSeatNumberRef = useRef(pendingSeatNumber);
  
  useEffect(() => { activeSeatRotationRef.current = activeSeatRotation; }, [activeSeatRotation]);
  useEffect(() => { pendingSeatSectionRef.current = pendingSeatSection; }, [pendingSeatSection]);
  useEffect(() => { pendingSeatRowRef.current = pendingSeatRow; }, [pendingSeatRow]);
  useEffect(() => { pendingSeatNumberRef.current = pendingSeatNumber; }, [pendingSeatNumber]);
  
  // Seat Template hooks
  const { templates, activeTemplate, setActiveTemplate, loading: templatesLoading, refreshTemplates } = useSeatTemplates();
  const activeTemplateRef = useRef(activeTemplate);
  useEffect(() => { activeTemplateRef.current = activeTemplate; }, [activeTemplate]);
  
  const { 
    placedSeats, 
    placeSeat, 
    updateSeat, 
    deleteSeat, 
    cloneSeat, 
    generateRow, 
    assignTicket,
    bulkPublish,
    refreshSeats 
  } = usePlacedSeats(activeChartId);
  
  // Selected placed seat
  const [selectedPlacedSeatId, setSelectedPlacedSeatId] = useState(null);
  const selectedPlacedSeat = useMemo(() => 
    placedSeats.find(s => s.id === selectedPlacedSeatId),
    [placedSeats, selectedPlacedSeatId]
  );
  const [seatGeometryDeleting, setSeatGeometryDeleting] = useState(false);
  const [seatGeometryDeleteError, setSeatGeometryDeleteError] = useState(null);
  const [seatGeometryStatusUpdating, setSeatGeometryStatusUpdating] = useState(false);
  const [seatGeometryStatusError, setSeatGeometryStatusError] = useState(null);
  
  // Ghost preview cursor position
  const [cursorPosition, setCursorPosition] = useState(null);
  const [hoveredPlacedSeatId, setHoveredPlacedSeatId] = useState(null);
  
  // =========================================================================
  // HOOKS
  // =========================================================================
  const {
    transform,
    screenToImageCoords,
    findOverlayAtPoint
  } = useMapCoordinates(viewerRef, viewerSyncToken);
  
  const {
    selectedOverlayId,
    setSelectedOverlayId,
    showPropertiesPanel,
    setShowPropertiesPanel,
    selectOverlay,
    clearSelection,
    isDragging,
    dragMode,
    dragStart,
    startMove,
    startResize,
    startVertexEdit,
    calculateDragUpdate,
    endDrag,
    editingVertexIndex,
    isLocked
  } = useOverlaySelection();
  
  const {
    pushState,
    undo,
    redo,
    clearHistory,
    canUndo,
    canRedo,
    undoCount,
    redoCount
  } = useOverlayHistory(activeChartId);
  
  const {
    isSaving,
    isLoading,
    saveError,
    loadOverlays,
    saveOverlays,
    deleteOverlay
  } = useOverlayPersistence(activeChartId);
  
  const {
    PUBLICATION_STATUS,
    canEditGeometry,
    canEditMetadata,
    handlePublishedEdit
  } = usePublicationWorkflow();
  
  // Seat Inventory hook (new canonical system)
  const [authToken, setAuthToken] = useState(null);
  const {
    seats: inventorySeats,
    stats: inventoryStats,
    loading: inventoryLoading,
    error: inventoryError,
    fetchSeats: refreshInventory,
    createSeat: createInventorySeat,
    assignTicket: assignInventoryTicket,
    removeAssignment: removeInventoryAssignment,
    deleteSeat: deleteInventorySeat,
    updateSeatGeometry,
    previewRow: previewInventoryRow,
    confirmRow: confirmInventoryRow,
    getUnassignedTickets,
    getAssignmentDetail,
    verifyAssignment,
    reopenVerification,
    publishAssignment,
    unpublishAssignment,
    reassignTicket,
    clearError: clearInventoryError
  } = useSeatInventory(activeChartId, authToken);
  
  // ===== Seat Drafts (local, unsaved until Save) =====
  const {
    drafts,
    addDraft,
    updateDraft,
    removeDraft,
    duplicateDraft,
    saveOne: saveOneDraft,
    saveAllDrafts,
    saving: draftSaving,
    suggestNextSeat,
    rememberMeta,
  } = useSeatDrafts(activeChartId, authToken, { onSaved: refreshInventory });
  const [selectedDraftId, setSelectedDraftId] = useState(null);
  const [drawPreview, setDrawPreview] = useState(null);
  const [spaceHeld, setSpaceHeld] = useState(false);
  const [isPanningPointer, setIsPanningPointer] = useState(false);
  const [guidesVisible, setGuidesVisible] = useState(true);
  const selectedDraft = useMemo(
    () => drafts.find(d => d.id === selectedDraftId) || null,
    [drafts, selectedDraftId]
  );
  
  // Seat Inventory UI state
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [assignmentTargetSeat, setAssignmentTargetSeat] = useState(null);
  const [unassignedTickets, setUnassignedTickets] = useState([]);
  const [showAssignmentManager, setShowAssignmentManager] = useState(false);
  const [assignmentManagerSeat, setAssignmentManagerSeat] = useState(null);
  const [showRowGenerator, setShowRowGenerator] = useState(false);
  const [rowPreviewData, setRowPreviewData] = useState(null);
  const [controlSeat1, setControlSeat1] = useState(null);
  const [controlSeat2, setControlSeat2] = useState(null);
  
  // Use seat inventory data instead of legacy placed seats when available
  const displaySeats = useMemo(() => {
    // Prefer new inventory system if seats exist
    if (inventorySeats.length > 0) {
      return inventorySeats;
    }
    // Fallback to legacy placed seats
    return placedSeats.map(s => ({
      ...s,
      native_x: s.native_x,
      native_y: s.native_y,
      display_state: 'unassigned',
      is_assigned: false
    }));
  }, [inventorySeats, placedSeats]);
  
  // The persisted canonical seat currently selected (if any) - distinct from
  // legacy selectedPlacedSeat and from the local unsaved selectedDraft.
  const selectedInventorySeat = useMemo(
    () => inventorySeats.find(s => s.id === selectedPlacedSeatId) || null,
    [inventorySeats, selectedPlacedSeatId]
  );
  
  // Selecting a persisted seat should not leave an overlay or draft panel
  // open at the same time (only one properties panel visible at once).
  const handleSelectInventorySeat = useCallback((seatId) => {
    setSelectedPlacedSeatId(seatId);
    setSelectedDraftId(null);
    clearSelection();
    setSeatGeometryDeleteError(null);
  }, [clearSelection]);
  
  // Computed
  const selectedOverlay = useMemo(() => 
    overlays.find(o => o.id === selectedOverlayId), 
    [overlays, selectedOverlayId]
  );
  
  const chartOverlays = useMemo(() => 
    overlays.filter(o => o.chartId === activeChartId), 
    [overlays, activeChartId]
  );
  
  const publishedCount = useMemo(() => 
    chartOverlays.filter(o => o.publication_status === PUBLICATION_STATUS.PUBLISHED).length,
    [chartOverlays, PUBLICATION_STATUS.PUBLISHED]
  );
  
  // Refs to avoid stale closures in event handlers
  const activeToolRef = useRef(activeTool);
  const activeCategoryRef = useRef(activeCategory);
  const activeChartIdRef = useRef(activeChartId);
  const currentDrawingRef = useRef(currentDrawing);
  const selectedOverlayIdRef = useRef(selectedOverlayId);
  const overlaysRef = useRef(overlays);
  const isDraggingRef = useRef(isDragging);
  const dragStartRef = useRef(dragStart);
  
  useEffect(() => { activeToolRef.current = activeTool; }, [activeTool]);
  useEffect(() => { activeCategoryRef.current = activeCategory; }, [activeCategory]);
  useEffect(() => { activeChartIdRef.current = activeChartId; }, [activeChartId]);
  useEffect(() => { currentDrawingRef.current = currentDrawing; }, [currentDrawing]);
  useEffect(() => { selectedOverlayIdRef.current = selectedOverlayId; }, [selectedOverlayId]);
  useEffect(() => { overlaysRef.current = overlays; }, [overlays]);
  useEffect(() => { isDraggingRef.current = isDragging; }, [isDragging]);
  useEffect(() => { dragStartRef.current = dragStart; }, [dragStart]);
  
  // Refs for the seat-draft / pan interaction handlers
  const draftsRef = useRef(drafts);
  const selectedDraftIdRef = useRef(selectedDraftId);
  const spaceHeldRef = useRef(spaceHeld);
  const transformRef = useRef(transform);
  const drawStartRef = useRef(null);   // native coords where a draw drag started
  const draftDragRef = useRef(null);   // { mode:'move'|'resize', handle, start, orig }
  const interactionLayerRef = useRef(null);
  const selectedPlacedSeatIdRef = useRef(selectedPlacedSeatId);
  const inventorySeatsRef = useRef(inventorySeats);
  
  useEffect(() => { draftsRef.current = drafts; }, [drafts]);
  useEffect(() => { selectedDraftIdRef.current = selectedDraftId; }, [selectedDraftId]);
  useEffect(() => { spaceHeldRef.current = spaceHeld; }, [spaceHeld]);
  useEffect(() => { transformRef.current = transform; }, [transform]);
  useEffect(() => { selectedPlacedSeatIdRef.current = selectedPlacedSeatId; }, [selectedPlacedSeatId]);
  useEffect(() => { inventorySeatsRef.current = inventorySeats; }, [inventorySeats]);
  
  // Initialize auth token from localStorage
  useEffect(() => {
    const token = localStorage.getItem('ownerToken');
    if (token) {
      setAuthToken(token);
    }
  }, []);
  
  // P0 FIX: Track component mount state for safe async cleanup
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  
  // P0 FIX: Safe viewer check - returns true only if viewer is fully initialized
  const isViewerValid = useCallback((viewer) => {
    try {
      return viewer && 
             viewer.isOpen && 
             viewer.isOpen() && 
             viewer.world && 
             viewer.world.getItemCount() > 0 &&
             viewer.drawer;
    } catch (e) {
      return false;
    }
  }, []);
  
  // =========================================================================
  // OpenSeadragon Initialization
  // =========================================================================
  useEffect(() => {
    if (!containerRef.current || viewerRef.current) return;
    
    // P0 FIX: Track if this effect instance is still valid
    let effectCleanedUp = false;
    
    const osdViewer = OpenSeadragon({
      element: containerRef.current,
      tileSources: { type: 'image', url: activeChart.url, buildPyramid: false },
      prefixUrl: 'https://cdnjs.cloudflare.com/ajax/libs/openseadragon/4.1.0/images/',
      showNavigator: true,
      navigatorPosition: 'BOTTOM_RIGHT',
      navigatorSizeRatio: 0.12,
      showNavigationControl: false,
      animationTime: 0.3,
      maxZoomPixelRatio: 4,
      minZoomLevel: 0.5,
      maxZoomLevel: 15,
      visibilityRatio: 0.8,
      gestureSettingsMouse: { clickToZoom: false, dblClickToZoom: true, scrollToZoom: true },
      // Pure white background
      backgroundColor: '#ffffff'
    });
    
    viewerRef.current = osdViewer;
    
    osdViewer.addHandler('open', () => {
      // P0 FIX: Guard against stale callback
      if (effectCleanedUp || !isMountedRef.current) return;
      
      // Expose viewer for coordinate-based tooling/debugging
      if (typeof window !== 'undefined') window.__mapEditorViewer = osdViewer;
      
      const tiledImage = osdViewer.world.getItemAt(0);
      if (tiledImage && activeChart.arenaCrop) {
        const crop = activeChart.arenaCrop;
        const bounds = tiledImage.imageToViewportRectangle(crop.x, crop.y, crop.width, crop.height);
        osdViewer.viewport.fitBounds(bounds, true);
      }
      setIsReady(true);
      // Viewer + tiled image are now confirmed ready - let useMapCoordinates
      // attach its sync handlers and compute the real transform.
      setViewerSyncToken(t => t + 1);
    });
    
    osdViewer.addHandler('zoom', (e) => {
      // P0 FIX: Guard zoom handler
      if (!effectCleanedUp && isMountedRef.current) {
        setCurrentZoom(e.zoom);
      }
    });
    
    // Capture the viewer instance for proper cleanup
    const viewerToCleanup = osdViewer;
    
    return () => {
      // P0 FIX: Mark effect as cleaned up
      effectCleanedUp = true;
      
      // PROPER LIFECYCLE: Use captured viewer instance, not ref
      if (viewerToCleanup) {
        try {
          viewerToCleanup.removeAllHandlers();
          viewerToCleanup.close();
          viewerToCleanup.destroy();
        } catch (e) {
          // Ignore errors during cleanup
        }
        if (viewerRef.current === viewerToCleanup) {
          viewerRef.current = null;
        }
      }
    };
  }, []);
  
  // Enable OSD mouse navigation ONLY when panning (Hand tool or Space held).
  // In every other mode the interaction layer captures pointer events for
  // drawing / selecting, and OSD nav stays off.
  useEffect(() => {
    if (!viewerRef.current) return;
    const panMode = activeTool === TOOLS.HAND || spaceHeld;
    viewerRef.current.setMouseNavEnabled(panMode);
  }, [activeTool, spaceHeld]);
  
  // ===== Spacebar temporary pan (with form-field safety) =====
  useEffect(() => {
    const isFormTarget = (el) => {
      if (!el) return false;
      const tag = (el.tagName || '').toLowerCase();
      return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
    };
    const handleKeyDown = (e) => {
      if (e.code === 'Space' || e.key === ' ') {
        if (isFormTarget(e.target)) return; // allow typing a space
        e.preventDefault();
        if (!spaceHeldRef.current) setSpaceHeld(true);
      }
    };
    const handleKeyUp = (e) => {
      if (e.code === 'Space' || e.key === ' ') {
        setSpaceHeld(false);
        setIsPanningPointer(false);
      }
    };
    const resetPan = () => { setSpaceHeld(false); setIsPanningPointer(false); };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', resetPan);
    document.addEventListener('visibilitychange', resetPan);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', resetPan);
      document.removeEventListener('visibilitychange', resetPan);
    };
  }, []);
  
  // ===== Mouse-wheel zoom over the interaction layer (no page scroll) =====
  // OSD handles wheel on its own canvas when nav is enabled (Hand/Space). During
  // drawing/select the interaction layer sits on top, so we forward wheel to OSD.
  useEffect(() => {
    const el = interactionLayerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      const viewer = viewerRef.current;
      if (!viewer || !viewer.viewport) return;
      e.preventDefault();
      e.stopPropagation();
      try {
        const rect = viewer.container.getBoundingClientRect();
        const pixel = new OpenSeadragon.Point(e.clientX - rect.left, e.clientY - rect.top);
        const vpPoint = viewer.viewport.pointFromPixel(pixel);
        const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
        viewer.viewport.zoomBy(factor, vpPoint);
        viewer.viewport.applyConstraints();
      } catch (err) { /* viewer not ready */ }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [isReady]);
  
  // ===== Warn before leaving with unsaved seat drafts =====
  useEffect(() => {
    const handler = (e) => {
      if (drafts.length > 0) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [drafts.length]);
  
  // ===== Dev/test helpers: deterministic native<->client coordinate conversion =====
  useEffect(() => {
    if (!isReady) return;
    window.__mapEditorNativeToClient = (nx, ny) => {
      const viewer = viewerRef.current;
      const ti = viewer?.world?.getItemAt(0);
      if (!ti) return null;
      const vp = ti.imageToViewportCoordinates(new OpenSeadragon.Point(nx, ny));
      const px = viewer.viewport.pixelFromPoint(vp, true);
      const rect = viewer.element.getBoundingClientRect();
      return { x: rect.left + px.x, y: rect.top + px.y };
    };
    window.__mapEditorClientToNative = (cx, cy) => screenToImageCoords(cx, cy);
    return () => {
      try { delete window.__mapEditorNativeToClient; delete window.__mapEditorClientToNative; } catch (e) { /* noop */ }
    };
  }, [isReady, screenToImageCoords]);
  
  // Load chart on change
  useEffect(() => {
    // P0 FIX: Guard against invalid viewer state
    if (!isMountedRef.current || !isViewerValid(viewerRef.current) || !isReady) return;
    
    // P0 FIX: Track if this effect instance is still valid
    let chartEffectCleanedUp = false;
    
    viewerRef.current.open({ type: 'image', url: activeChart.url, buildPyramid: false });
    viewerRef.current.addOnceHandler('open', () => {
      // P0 FIX: Guard against stale callback
      if (chartEffectCleanedUp || !isMountedRef.current || !isViewerValid(viewerRef.current)) return;
      
      const tiledImage = viewerRef.current.world.getItemAt(0);
      if (tiledImage && activeChart.arenaCrop) {
        const crop = activeChart.arenaCrop;
        const bounds = tiledImage.imageToViewportRectangle(crop.x, crop.y, crop.width, crop.height);
        viewerRef.current.viewport.fitBounds(bounds, true);
      }
      // New chart's tiled image is open and fitted - re-sync coordinate
      // transform for the new image (old chart's geometry/handlers must not
      // linger at the previous chart's scale/position).
      setViewerSyncToken(t => t + 1);
    });
    
    // Clear selection when switching charts
    clearSelection();
    
    // P0 FIX: Cleanup function
    return () => {
      chartEffectCleanedUp = true;
    };
  }, [activeChartId, activeChart, isReady, clearSelection, isViewerValid]);
  
  // Load overlays from server when chart changes
  useEffect(() => {
    if (!isReady) return;
    
    const load = async () => {
      const { overlays: loaded, error } = await loadOverlays();
      if (!error && loaded.length > 0) {
        loaded.forEach(o => persistedOverlayIdsRef.current.add(o.id));
        setOverlays(prev => {
          const otherCharts = prev.filter(o => o.chartId !== activeChartId);
          return [...otherCharts, ...loaded];
        });
        setSaveMessage(`Loaded ${loaded.length} overlays`);
        setTimeout(() => setSaveMessage(''), 2000);
        // NOTE: History is preserved per-chart. The useOverlayHistory hook
        // now properly syncs counts when chartId changes via its useEffect.
        // Do NOT call clearHistory() here - it wipes valid user history.
      }
    };
    
    load();
  }, [activeChartId, isReady, loadOverlays]);
  
  // =========================================================================
  // Pointer Handlers
  // =========================================================================
  
  // Hit-test helpers for local seat drafts (native image coordinates)
  const pointInDraft = useCallback((coords, d) => {
    return coords.x >= d.native_x - d.width / 2 && coords.x <= d.native_x + d.width / 2 &&
           coords.y >= d.native_y - d.height / 2 && coords.y <= d.native_y + d.height / 2;
  }, []);
  
  const hitDraftHandle = useCallback((coords, d) => {
    const scale = transformRef.current?.scale || 1;
    const tol = Math.max(6, 10 / scale); // native tolerance ~ constant screen px
    const handles = getDraftHandles(d);
    for (const [name, pt] of Object.entries(handles)) {
      if (Math.abs(coords.x - pt.x) <= tol && Math.abs(coords.y - pt.y) <= tol) {
        return name;
      }
    }
    return null;
  }, []);
  
  const handlePointerDown = useCallback((e) => {
    // Panning is handled by OSD (Hand tool or Space held) — ignore here.
    if (spaceHeldRef.current || activeToolRef.current === TOOLS.HAND) return;
    if (e.button !== undefined && e.button !== 0) return; // primary button only
    
    const tool = activeToolRef.current;
    const category = activeCategoryRef.current;
    const chartId = activeChartIdRef.current;
    const coords = screenToImageCoords(e.clientX, e.clientY);
    
    // Capture pointer for drag operations
    if (e.currentTarget && e.pointerId !== undefined) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    
    // ---- DRAW SEAT BOX: begin click-drag rectangle (native coords) ----
    if (tool === TOOLS.DRAW_SEAT) {
      drawStartRef.current = { x: coords.x, y: coords.y };
      setDrawPreview({ x: coords.x, y: coords.y, width: 0, height: 0 });
      return;
    }
    
    // ---- PLACE TEMPLATE SEAT: single-click local draft at fixed size ----
    if (tool === TOOLS.PLACE_SEAT) {
      const template = activeTemplateRef.current;
      const w = template?.geometry?.width || 17;
      const h = template?.geometry?.height || 14;
      const d = addDraft({
        native_x: coords.x,
        native_y: coords.y,
        width: w,
        height: h,
        rotation: activeSeatRotationRef.current || 0,
        template_id: template?.id || null,
        section: pendingSeatSectionRef.current,
        row: pendingSeatRowRef.current,
        seat: pendingSeatNumberRef.current,
      });
      setSelectedDraftId(d.id);
      clearSelection();
      return;
    }
    
    // ---- SELECT: prioritise local seat drafts (resize handle → body) ----
    if (tool === TOOLS.SELECT) {
      const selId = selectedDraftIdRef.current;
      const currentDrafts = draftsRef.current;
      const selDraft = currentDrafts.find(d => d.id === selId);
      
      // 1) Resize / rotate handle of the selected draft
      if (selDraft) {
        const handle = hitDraftHandle(coords, selDraft);
        if (handle) {
          draftDragRef.current = {
            mode: handle === 'rotate' ? 'rotate' : 'resize',
            handle,
            start: coords,
            orig: { ...selDraft },
          };
          return;
        }
      }
      
      // 2) Click on a draft body → select + start move (topmost first)
      const hit = [...currentDrafts].reverse().find(d => pointInDraft(coords, d));
      if (hit) {
        setSelectedDraftId(hit.id);
        clearSelection();
        draftDragRef.current = { mode: 'move', start: coords, orig: { ...hit } };
        return;
      }
      
      // 3) Empty space → clear draft selection, fall through to overlay logic
      setSelectedDraftId(null);
      const selectedId = selectedOverlayIdRef.current;
      const currentOverlays = overlaysRef.current.filter(o => o.chartId === chartId);
      const clickedOverlay = findOverlayAtPoint(coords.x, coords.y, currentOverlays);
      
      if (clickedOverlay) {
        selectOverlay(clickedOverlay);
        if (clickedOverlay.isLocked) {
          setSaveMessage('Overlay is locked');
          setTimeout(() => setSaveMessage(''), 2000);
          return;
        }
        const publishCheck = handlePublishedEdit(clickedOverlay, 'move');
        if (publishCheck?.action === 'requires_unpublish') {
          setSaveMessage(publishCheck.message);
          setTimeout(() => setSaveMessage(''), 3000);
          return;
        }
        pushState(overlaysRef.current, HISTORY_ACTIONS.MOVE, { overlayId: clickedOverlay.id });
        startMove(clickedOverlay, coords);
      } else {
        clearSelection();
      }
      return;
    }
    
    // DRAWING TOOLS
    if (tool === TOOLS.DRAW_RECT) {
      pushState(overlaysRef.current, HISTORY_ACTIONS.CREATE);
      setCurrentDrawing({ type: 'rect', startX: coords.x, startY: coords.y, endX: coords.x, endY: coords.y });
    } else if (tool === TOOLS.PLACE_SEAT) {
      // New template-based seat placement
      const template = activeTemplateRef.current;
      if (!template) {
        setSaveMessage('No seat template selected');
        setTimeout(() => setSaveMessage(''), 2000);
        return;
      }
      
      // Get current seat placement values from refs
      const section = pendingSeatSectionRef.current;
      const row = pendingSeatRowRef.current;
      const seatNum = pendingSeatNumberRef.current;
      const rotation = activeSeatRotationRef.current;
      
      // Use template dimensions centered at click point
      const width = template.geometry?.width || 17;
      const height = template.geometry?.height || 14;
      
      // Place the seat using the API
      const seatData = {
        template_id: template.id,
        chart_id: chartId,
        section: section,
        row: row,
        seat: seatNum,
        native_x: coords.x,
        native_y: coords.y,
        rotation: rotation
      };
      
      placeSeat(seatData).then(result => {
        if (result.error) {
          setSaveMessage(`Error: ${result.error}`);
          setTimeout(() => setSaveMessage(''), 3000);
        } else {
          setSaveMessage(`Placed: ${section} R${row} S${seatNum}`);
          setTimeout(() => setSaveMessage(''), 2000);
          
          // Select the new seat
          setSelectedPlacedSeatId(result.seat?.id);
          
          // Auto-decrement seat number for next placement
          const nextSeat = parseInt(seatNum) - 1;
          if (nextSeat >= 1) {
            setPendingSeatNumber(String(nextSeat));
          }
        }
      });
    } else if (tool === TOOLS.DRAW_SEAT) {
      // Legacy arbitrary seat box (kept for backward compatibility)
      pushState(overlaysRef.current, HISTORY_ACTIONS.CREATE);
      const forcedCategory = getToolForcedCategory(TOOLS.DRAW_SEAT) || 'seat_area';
      const currentOverlays = overlaysRef.current;
      const newOverlay = {
        id: `overlay-${Date.now()}`,
        type: 'rect',
        chartId: chartId,
        category: forcedCategory,
        name: generateTempName('seat_box', currentOverlays),
        x: coords.x - 10,
        y: coords.y - 10,
        width: 20,
        height: 20,
        visible: true,
        publication_status: PUBLICATION_STATUS.DRAFT,
        has_unreviewed_changes: true
      };
      setOverlays(prev => [...prev, newOverlay]);
      selectOverlay(newOverlay);
    } else if (tool === TOOLS.PLACE_MARKER) {
      pushState(overlaysRef.current, HISTORY_ACTIONS.CREATE);
      const currentOverlays = overlaysRef.current;
      const newOverlay = {
        id: `overlay-${Date.now()}`,
        type: 'marker',
        chartId: chartId,
        category: category,
        name: generateTempName('marker', currentOverlays),
        x: coords.x,
        y: coords.y,
        visible: true,
        publication_status: PUBLICATION_STATUS.DRAFT,
        has_unreviewed_changes: true
      };
      setOverlays(prev => [...prev, newOverlay]);
      selectOverlay(newOverlay);
    } else if (tool === TOOLS.DRAW_POLYGON) {
      const drawing = currentDrawingRef.current;
      if (!drawing) {
        pushState(overlaysRef.current, HISTORY_ACTIONS.CREATE);
        setCurrentDrawing({ type: 'polygon', points: [{ x: coords.x, y: coords.y }] });
      } else {
        setCurrentDrawing(prev => ({ ...prev, points: [...prev.points, { x: coords.x, y: coords.y }] }));
      }
    }
  }, [screenToImageCoords, findOverlayAtPoint, selectOverlay, clearSelection, startMove, pushState, handlePublishedEdit, PUBLICATION_STATUS.DRAFT, addDraft, hitDraftHandle, pointInDraft]);
  
  const handlePointerMove = useCallback((e) => {
    const coords = screenToImageCoords(e.clientX, e.clientY);
    setMouseImageCoords(coords);
    
    // Update cursor position for ghost preview (screen coordinates)
    const rect = e.currentTarget.getBoundingClientRect();
    setCursorPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    
    // ---- Live Draw Seat Box preview ----
    if (drawStartRef.current) {
      const s = drawStartRef.current;
      setDrawPreview({
        x: Math.min(s.x, coords.x),
        y: Math.min(s.y, coords.y),
        width: Math.abs(coords.x - s.x),
        height: Math.abs(coords.y - s.y),
      });
      return;
    }
    
    // ---- Draft move / resize ----
    const dd = draftDragRef.current;
    if (dd) {
      const id = selectedDraftIdRef.current;
      const orig = dd.orig;
      if (dd.mode === 'move') {
        const dx = coords.x - dd.start.x;
        const dy = coords.y - dd.start.y;
        updateDraft(id, { native_x: orig.native_x + dx, native_y: orig.native_y + dy });
      } else if (dd.mode === 'rotate') {
        const ang = Math.atan2(coords.y - orig.native_y, coords.x - orig.native_x) * 180 / Math.PI;
        updateDraft(id, { rotation: Math.round(ang + 90) });
      } else if (dd.mode === 'resize' && !orig.size_locked) {
        let left = orig.native_x - orig.width / 2;
        let top = orig.native_y - orig.height / 2;
        let right = left + orig.width;
        let bottom = top + orig.height;
        const hnd = dd.handle;
        if (hnd.includes('e')) right = coords.x;
        if (hnd.includes('w')) left = coords.x;
        if (hnd.includes('s')) bottom = coords.y;
        if (hnd.includes('n')) top = coords.y;
        const newW = Math.max(2, right - left);
        const newH = Math.max(2, bottom - top);
        updateDraft(id, {
          width: newW,
          height: newH,
          native_x: left + newW / 2,
          native_y: top + newH / 2,
        });
      }
      return;
    }
    
    // Update rectangle drawing (generic overlay)
    const drawing = currentDrawingRef.current;
    if (drawing?.type === 'rect') {
      setCurrentDrawing(prev => prev ? ({ ...prev, endX: coords.x, endY: coords.y }) : null);
    }
    
    // Handle drag operations (move, resize, vertex edit)
    if (isDraggingRef.current && dragStartRef.current && selectedOverlayIdRef.current) {
      const selectedId = selectedOverlayIdRef.current;
      const overlay = overlaysRef.current.find(o => o.id === selectedId);
      
      if (overlay) {
        const updated = calculateDragUpdate(coords, overlay);
        if (updated) {
          setOverlays(prev => prev.map(o => o.id === selectedId ? updated : o));
        }
      }
    }
  }, [screenToImageCoords, calculateDragUpdate, updateDraft]);
  
  const handlePointerUp = useCallback((e) => {
    // Release pointer capture
    if (e.currentTarget && e.pointerId !== undefined) {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch (err) {
        // Ignore if not captured
      }
    }
    
    // ---- Finish Draw Seat Box → create a local draft ----
    if (drawStartRef.current) {
      const s = drawStartRef.current;
      const coords = screenToImageCoords(e.clientX, e.clientY);
      const width = Math.abs(coords.x - s.x);
      const height = Math.abs(coords.y - s.y);
      const cx = (s.x + coords.x) / 2;
      const cy = (s.y + coords.y) / 2;
      drawStartRef.current = null;
      setDrawPreview(null);
      
      const scale = transformRef.current?.scale || 1;
      const minNative = 5 / scale; // ~5 screen px threshold → ignore clicks
      if (width > minNative && height > minNative) {
        const template = activeTemplateRef.current;
        // Safety floor so a small drag still yields a usable, saveable box
        const safeWidth = Math.max(width, 6);
        const safeHeight = Math.max(height, 6);
        const d = addDraft({
          native_x: cx,
          native_y: cy,
          width: safeWidth,
          height: safeHeight,
          rotation: 0,
          template_id: template?.id || null,
          section: pendingSeatSectionRef.current,
          row: pendingSeatRowRef.current,
          seat: pendingSeatNumberRef.current,
        });
        setSelectedDraftId(d.id);
        setSaveMessage('Draft seat box created — label it, then Save');
        setTimeout(() => setSaveMessage(''), 2500);
      }
      return;
    }
    
    // ---- End draft move / resize ----
    if (draftDragRef.current) {
      draftDragRef.current = null;
      return;
    }
    
    // Finish rectangle drawing (generic overlay)
    const drawing = currentDrawingRef.current;
    if (drawing?.type === 'rect') {
      const x = Math.min(drawing.startX, drawing.endX);
      const y = Math.min(drawing.startY, drawing.endY);
      const width = Math.abs(drawing.endX - drawing.startX);
      const height = Math.abs(drawing.endY - drawing.startY);
      
      if (width > 5 && height > 5) {
        const chartId = activeChartIdRef.current;
        const category = activeCategoryRef.current;
        const currentOverlays = overlaysRef.current;
        const newOverlay = {
          id: `overlay-${Date.now()}`,
          type: 'rect',
          chartId: chartId,
          category: category,
          name: generateTempName('rect', currentOverlays),
          x, y, width, height,
          visible: true,
          publication_status: PUBLICATION_STATUS.DRAFT,
          has_unreviewed_changes: true
        };
        setOverlays(prev => [...prev, newOverlay]);
        selectOverlay(newOverlay);
      }
      setCurrentDrawing(null);
    }
    
    // End any drag operation
    endDrag();
  }, [selectOverlay, endDrag, PUBLICATION_STATUS.DRAFT, screenToImageCoords, addDraft]);
  
  const handleDoubleClick = useCallback(() => {
    const drawing = currentDrawingRef.current;
    if (drawing?.type === 'polygon' && drawing.points.length >= 3) {
      const chartId = activeChartIdRef.current;
      const category = activeCategoryRef.current;
      const currentOverlays = overlaysRef.current;
      const newOverlay = {
        id: `overlay-${Date.now()}`,
        type: 'polygon',
        chartId: chartId,
        category: category,
        name: generateTempName('polygon', currentOverlays),
        points: drawing.points,
        visible: true,
        publication_status: PUBLICATION_STATUS.DRAFT,
        has_unreviewed_changes: true
      };
      setOverlays(prev => [...prev, newOverlay]);
      selectOverlay(newOverlay);
      setCurrentDrawing(null);
    }
  }, [selectOverlay, PUBLICATION_STATUS.DRAFT]);
  
  // =========================================================================
  // Resize and Vertex Handlers (called from SVG layer)
  // =========================================================================
  const handleResizeMouseDown = useCallback((overlayId, handle, e) => {
    e.stopPropagation();
    const coords = screenToImageCoords(e.clientX, e.clientY);
    const overlay = overlaysRef.current.find(o => o.id === overlayId);
    
    if (!overlay) return;
    
    // Check if can edit
    if (overlay.isLocked) {
      setSaveMessage('Overlay is locked');
      setTimeout(() => setSaveMessage(''), 2000);
      return;
    }
    
    const publishCheck = handlePublishedEdit(overlay, 'resize');
    if (publishCheck?.action === 'requires_unpublish') {
      setSaveMessage(publishCheck.message);
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }
    
    pushState(overlaysRef.current, HISTORY_ACTIONS.RESIZE, { overlayId, handle });
    startResize(overlay, handle, coords);
  }, [screenToImageCoords, startResize, pushState, handlePublishedEdit]);
  
  const handleVertexMouseDown = useCallback((overlayId, vertexIndex, e) => {
    e.stopPropagation();
    const coords = screenToImageCoords(e.clientX, e.clientY);
    const overlay = overlaysRef.current.find(o => o.id === overlayId);
    
    if (!overlay) return;
    
    // Check if can edit
    if (overlay.isLocked) {
      setSaveMessage('Overlay is locked');
      setTimeout(() => setSaveMessage(''), 2000);
      return;
    }
    
    const publishCheck = handlePublishedEdit(overlay, 'vertex_edit');
    if (publishCheck?.action === 'requires_unpublish') {
      setSaveMessage(publishCheck.message);
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }
    
    pushState(overlaysRef.current, HISTORY_ACTIONS.VERTEX_EDIT, { overlayId, vertexIndex });
    startVertexEdit(overlay, vertexIndex, coords);
  }, [screenToImageCoords, startVertexEdit, pushState, handlePublishedEdit]);
  
  // =========================================================================
  // Seat Inventory Handlers
  // =========================================================================
  
  // Open assignment modal for an unassigned seat
  const handleOpenAssignModal = useCallback(async (seat) => {
    if (!seat) return;
    
    setAssignmentTargetSeat(seat);
    
    // Fetch unassigned tickets for this section
    const tickets = await getUnassignedTickets(seat.section);
    setUnassignedTickets(tickets);
    
    setShowAssignmentModal(true);
  }, [getUnassignedTickets]);
  
  // Handle ticket assignment
  const handleAssignTicket = useCallback(async (assignmentData) => {
    const result = await assignInventoryTicket(assignmentData);
    if (result) {
      setSaveMessage(`Assigned ticket to ${assignmentTargetSeat?.section} R${assignmentTargetSeat?.row} S${assignmentTargetSeat?.seat}`);
      setTimeout(() => setSaveMessage(''), 3000);
      setShowAssignmentModal(false);
      setAssignmentTargetSeat(null);
    }
  }, [assignInventoryTicket, assignmentTargetSeat]);
  
  // Open ticket details for assigned seat -> owner Manage Assignment modal
  const handleOpenTicketDetails = useCallback((seat) => {
    if (seat?.assignment_id) {
      setAssignmentManagerSeat(seat);
      setShowAssignmentManager(true);
    }
  }, []);

  const handleCloseAssignmentManager = useCallback(() => {
    setShowAssignmentManager(false);
    setAssignmentManagerSeat(null);
  }, []);

  const handleViewFullTicket = useCallback((ticket) => {
    if (!ticket) return;
    if (ticket.public_reference) {
      navigator.clipboard?.writeText(ticket.public_reference).catch(() => {});
    }
    setSaveMessage(`Reference ${ticket.public_reference || ''} copied. Opening Ticket Records...`);
    setTimeout(() => setSaveMessage(''), 3000);
    navigate('/owner-dashboard/tickets');
  }, [navigate]);
  
  // Row generator handlers
  const handlePreviewRow = useCallback(async (rowData) => {
    const preview = await previewInventoryRow(rowData);
    setRowPreviewData(preview);
  }, [previewInventoryRow]);
  
  const handleConfirmRow = useCallback(async (rowData) => {
    const result = await confirmInventoryRow(rowData);
    if (result) {
      setSaveMessage(`Created ${result.created_count} Draft seats`);
      setTimeout(() => setSaveMessage(''), 3000);
      setShowRowGenerator(false);
      setRowPreviewData(null);
      setControlSeat1(null);
      setControlSeat2(null);
    }
  }, [confirmInventoryRow]);
  
  const handleCancelRowPreview = useCallback(() => {
    setRowPreviewData(null);
  }, []);
  
  // Set control seat for row generator
  const handleSetControlSeat = useCallback((seatNumber, seat) => {
    if (seatNumber === 1) {
      setControlSeat1(seat);
    } else if (seatNumber === 2) {
      setControlSeat2(seat);
    }
  }, []);
  
  // =========================================================================
  // Overlay Management
  // =========================================================================
  const handleSelectOverlay = useCallback((overlay) => {
    selectOverlay(overlay);
    setActiveTool(TOOLS.SELECT);
  }, [selectOverlay]);
  
  const handleUpdateOverlay = useCallback((updated) => {
    pushState(overlays, HISTORY_ACTIONS.RENAME, { overlayId: updated.id });
    setOverlays(prev => prev.map(o => o.id === updated.id ? updated : o));
    setSaveMessage('Saved locally');
    setTimeout(() => setSaveMessage(''), 2000);
  }, [overlays, pushState]);
  
  // Deletes an overlay. Unsaved local objects are removed immediately with no
  // API call. Objects that already exist server-side are removed through the
  // governed DELETE endpoint first - only dropped from local state once the
  // server confirms it (or confirms it's already gone). Published/archived
  // objects are rejected by the server and kept in local state with an error.
  const handleDeleteOverlay = useCallback(async (id) => {
    const overlay = overlays.find(o => o.id === id);
    if (!overlay) return;
    
    const status = overlay.publication_status || 'draft';
    const label = overlay.name || `${overlay.type || 'overlay'} (untitled)`;
    const confirmed = window.confirm(
      `Delete this ${status} ${overlay.type || 'overlay'}?\n\nLabel: ${label}\nChart: ${activeChart?.label || activeChartId}\nStatus: ${status}`
    );
    if (!confirmed) return;
    
    if (persistedOverlayIdsRef.current.has(id)) {
      const { success, error } = await deleteOverlay(id);
      if (!success) {
        setSaveMessage(error || 'Delete failed - overlay was not removed');
        setTimeout(() => setSaveMessage(''), 4000);
        return; // Keep the object in local state - server delete did not succeed
      }
      persistedOverlayIdsRef.current.delete(id);
    }
    
    pushState(overlays, HISTORY_ACTIONS.DELETE, { overlayId: id });
    setOverlays(prev => prev.filter(o => o.id !== id));
    clearSelection();
  }, [overlays, pushState, clearSelection, deleteOverlay, activeChart, activeChartId]);
  
  const handleStatusChange = useCallback((id, newStatus) => {
    pushState(overlays, HISTORY_ACTIONS.STATUS_CHANGE, { overlayId: id, newStatus });
    setOverlays(prev => prev.map(o => o.id === id ? { ...o, publication_status: newStatus } : o));
  }, [overlays, pushState]);
  
  // Delete a PERSISTED canonical seat geometry (never a legacy placed_seat).
  // Server governs: 409 if actively assigned, 409 if approved/published
  // (must be set back to draft/calibrated first). Failure keeps the seat
  // selected with a visible error instead of removing it optimistically.
  const handleDeleteSeatGeometry = useCallback(async (seatId) => {
    const seat = inventorySeats.find(s => s.id === seatId);
    if (!seat) return;
    
    const status = seat.geometry_status || 'draft';
    const assignmentLabel = seat.is_assigned ? (seat.ticket_reference || 'linked ticket') : 'None';
    const confirmed = window.confirm(
      `Delete this seat geometry?\n\n` +
      `Chart: ${activeChart?.label || activeChartId}\n` +
      `Section: ${seat.section}\n` +
      `Row: ${seat.row}\n` +
      `Seat: ${seat.seat}\n` +
      `Status: ${status}\n` +
      `Assignment: ${assignmentLabel}\n\n` +
      `This deletes the map geometry only.\nIt does not delete a ticket record.`
    );
    if (!confirmed) return;
    
    setSeatGeometryDeleting(true);
    setSeatGeometryDeleteError(null);
    const res = await deleteInventorySeat(seatId);
    setSeatGeometryDeleting(false);
    
    if (res.ok) {
      setSelectedPlacedSeatId(null);
      setSaveMessage('Seat geometry deleted');
      setTimeout(() => setSaveMessage(''), 3000);
    } else {
      // Keep the seat selected and show the exact server error - do not
      // report success and do not remove the seat from the UI.
      setSeatGeometryDeleteError(res.error || 'Delete failed');
    }
  }, [inventorySeats, activeChart, activeChartId, deleteInventorySeat]);
  
  const handleUpdateSeatGeometryStatus = useCallback(async (seatId, newStatus) => {
    setSeatGeometryStatusUpdating(true);
    setSeatGeometryStatusError(null);
    const res = await updateSeatGeometry(seatId, { geometry_status: newStatus });
    setSeatGeometryStatusUpdating(false);
    if (res.ok) {
      setSaveMessage(`Seat geometry set to ${newStatus}`);
      setTimeout(() => setSaveMessage(''), 3000);
    } else {
      setSeatGeometryStatusError(res.error || 'Failed to update status');
    }
  }, [updateSeatGeometry]);
  
  // =========================================================================
  // Keyboard Handlers
  // =========================================================================
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore when typing in a form field
      const tag = (e.target?.tagName || '').toLowerCase();
      const inForm = tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable;
      
      // ---- Local seat draft shortcuts ----
      const draftId = selectedDraftIdRef.current;
      if (draftId && !inForm) {
        const d = draftsRef.current.find(x => x.id === draftId);
        if (d) {
          if (e.key === 'Delete' || e.key === 'Backspace') {
            e.preventDefault();
            removeDraft(draftId);
            setSelectedDraftId(null);
            return;
          }
          const step = e.shiftKey ? 5 : 1;
          if (e.key === 'ArrowLeft') { e.preventDefault(); updateDraft(draftId, { native_x: d.native_x - step }); return; }
          if (e.key === 'ArrowRight') { e.preventDefault(); updateDraft(draftId, { native_x: d.native_x + step }); return; }
          if (e.key === 'ArrowUp') { e.preventDefault(); updateDraft(draftId, { native_y: d.native_y - step }); return; }
          if (e.key === 'ArrowDown') { e.preventDefault(); updateDraft(draftId, { native_y: d.native_y + step }); return; }
        }
      }
      
      // ESC: Cancel drawing or clear selection
      if (e.key === 'Escape') {
        setCurrentDrawing(null);
        setDrawPreview(null);
        drawStartRef.current = null;
        draftDragRef.current = null;
        setSelectedDraftId(null);
        clearSelection();
      }
      
      // DELETE: Delete selected overlay (governed - may call the server)
      if (e.key === 'Delete' && selectedOverlayId) {
        handleDeleteOverlay(selectedOverlayId);
      }
      
      // DELETE / BACKSPACE: Delete selected PERSISTED seat geometry (governed)
      if ((e.key === 'Delete' || e.key === 'Backspace') && !inForm && !draftId && !selectedOverlayId && selectedPlacedSeatIdRef.current) {
        const invSeat = inventorySeatsRef.current.find(s => s.id === selectedPlacedSeatIdRef.current);
        if (invSeat) {
          e.preventDefault();
          handleDeleteSeatGeometry(selectedPlacedSeatIdRef.current);
        }
      }
      
      // Ctrl+Z / Cmd+Z: Undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        const result = undo(overlays);
        if (result) {
          setOverlays(result.state);
        }
      }
      
      // Ctrl+Shift+Z / Cmd+Shift+Z: Redo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        const result = redo(overlays);
        if (result) {
          setOverlays(result.state);
        }
      }
      
      // Ctrl+Y / Cmd+Y: Redo (alternative)
      if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
        e.preventDefault();
        const result = redo(overlays);
        if (result) {
          setOverlays(result.state);
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedOverlayId, overlays, clearSelection, pushState, undo, redo, removeDraft, updateDraft, handleDeleteOverlay, handleDeleteSeatGeometry]);
  
  // =========================================================================
  // Undo/Redo Handlers
  // =========================================================================
  const handleUndo = useCallback(() => {
    const result = undo(overlays);
    if (result) {
      setOverlays(result.state);
    }
  }, [overlays, undo]);
  
  const handleRedo = useCallback(() => {
    const result = redo(overlays);
    if (result) {
      setOverlays(result.state);
    }
  }, [overlays, redo]);
  
  // =========================================================================
  // Save/Load
  // =========================================================================
  const handleSaveToServer = useCallback(async () => {
    setSaveMessage('Saving...');
    const { success, error } = await saveOverlays(chartOverlays);
    
    if (success) {
      // These overlays now exist server-side - future deletes must call the API.
      chartOverlays.forEach(o => persistedOverlayIdsRef.current.add(o.id));
      setSaveMessage('Saved!');
    } else {
      setSaveMessage(error || 'Save failed');
    }
    setTimeout(() => setSaveMessage(''), 3000);
  }, [chartOverlays, saveOverlays]);
  
  // =========================================================================
  // View Control Handlers
  // =========================================================================
  
  /**
   * Fit Arena - Frame the usable arena and floor area (excludes title/margins)
   */
  const fitArena = useCallback(() => {
    if (!viewerRef.current) return;
    const tiledImage = viewerRef.current.world.getItemAt(0);
    if (!tiledImage) return;
    
    const crop = activeChart.arenaCrop;
    if (crop) {
      const bounds = tiledImage.imageToViewportRectangle(crop.x, crop.y, crop.width, crop.height);
      viewerRef.current.viewport.fitBounds(bounds, false);
    }
  }, [activeChart]);
  
  /**
   * Fit Full Chart - Show complete source image including titles and margins
   */
  const fitFullChart = useCallback(() => {
    if (!viewerRef.current) return;
    const tiledImage = viewerRef.current.world.getItemAt(0);
    if (!tiledImage) return;
    
    const full = activeChart.fullChartBounds || { x: 0, y: 0, width: activeChart.width, height: activeChart.height };
    const bounds = tiledImage.imageToViewportRectangle(full.x, full.y, full.width, full.height);
    viewerRef.current.viewport.fitBounds(bounds, false);
  }, [activeChart]);
  
  /**
   * Reset View - Restore owner-defined default for this chart (arena framing)
   */
  const resetView = useCallback(() => {
    // Default reset goes to arena view
    fitArena();
  }, [fitArena]);
  
  // =========================================================================
  // Seat Draft Handlers
  // =========================================================================
  const handleSaveAllDrafts = useCallback(async () => {
    if (drafts.length === 0) {
      setSaveMessage('No draft seats to save');
      setTimeout(() => setSaveMessage(''), 2000);
      return;
    }
    setSaveMessage('Saving draft seats…');
    const res = await saveAllDrafts();
    if (res.failed === 0) {
      setSaveMessage(`Saved ${res.saved} seat${res.saved !== 1 ? 's' : ''}`);
    } else {
      setSaveMessage(`Saved ${res.saved}, ${res.failed} failed (see red drafts)`);
    }
    setTimeout(() => setSaveMessage(''), 4000);
  }, [drafts.length, saveAllDrafts]);
  
  const handleSaveOneDraft = useCallback(async (id) => {
    const d = drafts.find(x => x.id === id);
    if (d) rememberMeta(d.section, d.row, d.seat);
    const res = await saveOneDraft(id);
    if (res.ok) {
      setSelectedDraftId(null);
      setSaveMessage('Seat saved');
    } else {
      setSaveMessage(`Save failed: ${res.error}`);
    }
    setTimeout(() => setSaveMessage(''), 3500);
  }, [drafts, saveOneDraft, rememberMeta]);
  
  const handleDuplicateDraft = useCallback((id, offset) => {
    const src = drafts.find(x => x.id === id);
    let off = offset;
    if (!off) {
      const w = src?.width || 17;
      off = { x: w + 2, y: 0 };
    }
    const created = duplicateDraft(id, off);
    if (created) {
      setSelectedDraftId(created.id);
      setSaveMessage('Draft duplicated');
      setTimeout(() => setSaveMessage(''), 2000);
    }
  }, [duplicateDraft, drafts]);
  
  const handleDuplicateDirection = useCallback((id, direction) => {
    const src = drafts.find(x => x.id === id);
    if (!src) return;
    const gapX = (src.width || 17) + 2;
    const gapY = (src.height || 14) + 2;
    const map = {
      left: { x: -gapX, y: 0 },
      right: { x: gapX, y: 0 },
      up: { x: 0, y: -gapY },
      down: { x: 0, y: gapY },
    };
    handleDuplicateDraft(id, map[direction] || { x: gapX, y: 0 });
  }, [drafts, handleDuplicateDraft]);
  
  // Save a manually-drawn draft as a reusable seat template (new revision-friendly)
  const handleSaveAsTemplate = useCallback(async (id) => {
    const d = drafts.find(x => x.id === id);
    if (!d) return;
    const defaultName = 'Arena Standard Seat';
    const name = window.prompt('Template name', defaultName);
    if (!name) return;
    try {
      const res = await fetch(`${API_BASE}/api/seat-map/seat-templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        credentials: 'include',
        body: JSON.stringify({
          name,
          geometry: {
            type: 'rect',
            width: Math.round(d.width * 100) / 100,
            height: Math.round(d.height * 100) / 100,
            anchor_x: 0.5,
            anchor_y: 0.5,
          },
          default_rotation: d.rotation || 0,
          compatible_charts: [activeChartId],
        }),
      });
      if (res.ok) {
        const data = await res.json();
        const tpl = data.template || data;
        await refreshTemplates();
        if (tpl?.id) {
          setActiveTemplate(tpl);
          updateDraft(id, { template_id: tpl.id });
        }
        setSaveMessage(`Template "${name}" saved (${Math.round(d.width)}×${Math.round(d.height)})`);
      } else {
        const err = await res.json().catch(() => ({}));
        setSaveMessage(`Template save failed: ${err.detail || res.status}`);
      }
    } catch (e) {
      setSaveMessage(`Template save error: ${e.message}`);
    }
    setTimeout(() => setSaveMessage(''), 4000);
  }, [drafts, authToken, activeChartId, refreshTemplates, setActiveTemplate, updateDraft]);
  
  const handleDeleteDraft = useCallback((id) => {
    removeDraft(id);
    setSelectedDraftId(null);
  }, [removeDraft]);
  
  const handleChangeDraft = useCallback((id, changes) => {
    updateDraft(id, { ...changes, error: null });
    if ('section' in changes || 'row' in changes || 'seat' in changes) {
      const d = drafts.find(x => x.id === id);
      rememberMeta(
        changes.section ?? d?.section,
        changes.row ?? d?.row,
        changes.seat ?? d?.seat
      );
    }
  }, [updateDraft, drafts, rememberMeta]);
  
  // =========================================================================
  // Render
  // =========================================================================
  
  // Recalculate fit when panels open/close
  useEffect(() => {
    if (viewerRef.current && isReady && viewerRef.current.isOpen()) {
      setTimeout(() => {
        try {
          viewerRef.current.viewport.resize();
          viewerRef.current.forceRedraw();
        } catch (e) {
          // Viewer may not be fully initialized
        }
      }, 300);
    }
  }, [leftPanelCollapsed, isReady]);
  
  return (
    <div className="map-editor-shell" data-testid="map-editor-studio">
      {/* Toolbar Row */}
      <header className="map-editor-toolbar">
        {/* Chart Tabs */}
        <div className="chart-tabs">
          {Object.values(CHARTS).map(chart => (
            <button 
              key={chart.id} 
              onClick={() => setActiveChartId(chart.id)} 
              className={`chart-tab ${activeChartId === chart.id ? 'chart-tab--active' : ''}`}
              data-testid={`chart-tab-${chart.id}`}
            >
              {chart.label}
            </button>
          ))}
        </div>
        
        <div className="toolbar-spacer" />
        
        {/* View Controls */}
        <div className="toolbar-group">
          <button onClick={fitArena} className="toolbar-btn" title="Frame arena" data-testid="fit-arena-btn">
            Fit Arena
          </button>
          <button onClick={fitFullChart} className="toolbar-btn" title="Full chart" data-testid="fit-full-chart-btn">
            Full Chart
          </button>
          <button onClick={resetView} className="toolbar-btn" title="Reset view" data-testid="reset-view-btn">
            Reset
          </button>
        </div>
        
        <div className="toolbar-divider" />
        
        {/* Guides visibility */}
        <div className="toolbar-group">
          <label style={{ fontSize: 11, color: 'rgba(232,224,213,0.7)', marginRight: 4 }}>Guides</label>
          <select
            value={guidesVisible ? 'aisles' : 'hidden'}
            onChange={(e) => setGuidesVisible(e.target.value !== 'hidden')}
            className="category-select"
            style={{ width: 'auto', minWidth: 120 }}
            data-testid="guides-visibility-select"
            title="Show or hide owner placement guides"
          >
            <option value="aisles">Aisles &amp; Walkways</option>
            <option value="hidden">Hide Guides</option>
          </select>
        </div>
        
        <div className="toolbar-divider" />
        
        {/* Zoom Controls */}
        <div className="toolbar-group">
          <button onClick={() => viewerRef.current?.viewport.zoomBy(1.5)} className="toolbar-btn toolbar-btn--square" title="Zoom in">+</button>
          <button onClick={() => viewerRef.current?.viewport.zoomBy(0.67)} className="toolbar-btn toolbar-btn--square" title="Zoom out">−</button>
        </div>
        
        <div className="toolbar-divider" />
        
        {/* Save */}
        <button 
          onClick={handleSaveAllDrafts} 
          disabled={draftSaving || drafts.length === 0}
          className="toolbar-btn toolbar-btn--primary"
          data-testid="save-seats-btn"
          title="Save all unsaved seat drafts"
        >
          {draftSaving ? 'Saving…' : `Save Seats${drafts.length ? ` (${drafts.length})` : ''}`}
        </button>
        
        <button 
          onClick={handleSaveToServer} 
          disabled={isSaving}
          className="toolbar-btn"
          data-testid="save-all-btn"
        >
          {isSaving ? 'Saving...' : 'Save Zones'}
        </button>
        
        {saveMessage && <span className="save-message">{saveMessage}</span>}
      </header>
      
      {/* Workspace (map + panels) */}
      <div className="map-editor-workspace">
        {/* OpenSeadragon Canvas */}
        <div ref={containerRef} className="map-editor-canvas" />
        
        {/* Interaction Layer - captures pointer for draw/select; releases to OSD when panning */}
        <div 
          ref={interactionLayerRef}
          className="map-interaction-layer"
          style={{ 
            cursor: (activeTool === TOOLS.HAND || spaceHeld)
              ? (isPanningPointer ? 'grabbing' : 'grab')
              : (activeTool !== TOOLS.SELECT ? 'crosshair' : 'default'),
            pointerEvents: (activeTool === TOOLS.HAND || spaceHeld) ? 'none' : 'auto'
          }}
          onPointerDown={(e) => { if (activeTool === TOOLS.HAND || spaceHeld) setIsPanningPointer(true); handlePointerDown(e); }}
          onPointerMove={handlePointerMove}
          onPointerUp={(e) => { setIsPanningPointer(false); handlePointerUp(e); }}
          onPointerCancel={() => { drawStartRef.current = null; draftDragRef.current = null; setDrawPreview(null); setIsPanningPointer(false); }}
          onDoubleClick={handleDoubleClick}
          data-testid="interaction-layer"
        />
        
        {/* SVG Overlay Layer */}
        <OverlayLayer
          overlays={chartOverlays}
          selectedId={selectedOverlayId}
          onSelect={handleSelectOverlay}
          transform={transform}
          currentDrawing={currentDrawing}
          activeTool={activeTool}
          activeCategory={activeCategory}
          editingVertexIndex={editingVertexIndex}
          onVertexMouseDown={handleVertexMouseDown}
          onResizeMouseDown={handleResizeMouseDown}
          isSelectMode={activeTool === TOOLS.SELECT}
        />
        
        {/* Placed Seats Layer - Now using Seat Inventory system */}
        {transform && (
          <svg
            className="placed-seats-svg-layer"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: activeTool === TOOLS.SELECT ? 'auto' : 'none',
              zIndex: 15
            }}
          >
            {/* Use SeatInventoryLayer for new canonical seats */}
            {inventorySeats.length > 0 ? (
              <SeatInventoryLayer
                seats={displaySeats}
                template={activeTemplate}
                transform={transform}
                selectedSeatId={selectedPlacedSeatId}
                onSelectSeat={handleSelectInventorySeat}
                onOpenAssignModal={handleOpenAssignModal}
                onOpenTicketDetails={handleOpenTicketDetails}
                hoveredSeatId={hoveredPlacedSeatId}
                onHoverSeat={setHoveredPlacedSeatId}
                isOwner={true}
                showLabels={true}
              />
            ) : (
              // Fallback to legacy PlacedSeatsLayer
              <PlacedSeatsLayer
                placedSeats={placedSeats.filter(s => s.chart_id === activeChartId)}
                templates={templates}
                selectedSeatId={selectedPlacedSeatId}
                onSelectSeat={setSelectedPlacedSeatId}
                transform={transform}
                hoveredSeatId={hoveredPlacedSeatId}
                onHoverSeat={setHoveredPlacedSeatId}
              />
            )}
            
            {/* Ghost Preview Cursor */}
            <GhostPreviewCursor
              template={activeTemplate}
              transform={transform}
              cursorPosition={cursorPosition}
              rotation={activeSeatRotation}
              visible={activeTool === TOOLS.PLACE_SEAT && !!cursorPosition}
            />
          </svg>
        )}
        
        {/* Owner-only aisle / walkway exclusion shading (native-coord SVG, locked to OSD) */}
        <AisleZoneLayer
          viewer={viewerRef.current}
          chartId={activeChartId}
          chartDimensions={{ width: activeChart.width, height: activeChart.height }}
          authToken={authToken}
          visible={guidesVisible}
        />
        
        {/* Local unsaved seat drafts + live draw preview (native-coord SVG, locked to OSD) */}
        <DraftSeatOverlay
          viewer={viewerRef.current}
          chartDimensions={{ width: activeChart.width, height: activeChart.height }}
          drafts={drafts}
          selectedId={selectedDraftId}
          preview={drawPreview}
          scale={transform?.scale || 1}
        />
        
        {/* Left Rail - Tools & Overlays */}
        {!leftPanelCollapsed ? (
          <aside className="map-editor-left-rail">
            {/* Collapse Button */}
            <button 
              className="rail-collapse-btn"
              onClick={() => setLeftPanelCollapsed(true)}
              title="Hide panels"
            >
              ‹
            </button>
            
            {/* Drawing Tools Panel */}
            <div className="tool-panel">
              <div className="panel-header">
                <span>Editor Tools</span>
              </div>
              <div className="tool-buttons">
                <button 
                  onClick={() => setActiveTool(TOOLS.SELECT)}
                  className={`tool-btn ${activeTool === TOOLS.SELECT ? 'tool-btn--active' : ''}`}
                  title="Select Mode"
                  data-testid="tool-select-btn"
                >
                  ↖ Select
                </button>
                <button 
                  onClick={() => setActiveTool(TOOLS.HAND)}
                  className={`tool-btn ${activeTool === TOOLS.HAND ? 'tool-btn--active' : ''}`}
                  title="Hand / Pan (or hold Space)"
                  data-testid="tool-hand-btn"
                >
                  ✋ Hand
                </button>
                <button 
                  onClick={() => setActiveTool(TOOLS.DRAW_SEAT)}
                  className={`tool-btn ${activeTool === TOOLS.DRAW_SEAT ? 'tool-btn--active' : ''}`}
                  title="Draw Seat Box (click-drag)"
                  data-testid="tool-draw-seat-btn"
                >
                  ▭ Draw Seat Box
                </button>
                <button 
                  onClick={() => setActiveTool(TOOLS.PLACE_SEAT)}
                  className={`tool-btn ${activeTool === TOOLS.PLACE_SEAT ? 'tool-btn--active' : ''}`}
                  title="Place Template Seat (single-click)"
                  data-testid="tool-place-seat-btn"
                >
                  ⬚ Place Template Seat
                </button>
                <button 
                  onClick={() => setActiveTool(TOOLS.DRAW_RECT)}
                  className={`tool-btn ${activeTool === TOOLS.DRAW_RECT ? 'tool-btn--active' : ''}`}
                  title="Draw Rectangle (zone)"
                  data-testid="tool-rect-btn"
                >
                  ▢ Rectangle
                </button>
                <button 
                  onClick={() => setActiveTool(TOOLS.DRAW_POLYGON)}
                  className={`tool-btn ${activeTool === TOOLS.DRAW_POLYGON ? 'tool-btn--active' : ''}`}
                  title="Draw Polygon"
                  data-testid="tool-polygon-btn"
                >
                  ⬡ Polygon
                </button>
                <button 
                  onClick={() => setActiveTool(TOOLS.PLACE_MARKER)}
                  className={`tool-btn ${activeTool === TOOLS.PLACE_MARKER ? 'tool-btn--active' : ''}`}
                  title="Place Marker"
                  data-testid="tool-marker-btn"
                >
                  ● Marker
                </button>
              </div>
              
              {/* Category Select */}
              <select 
                value={activeCategory}
                onChange={(e) => setActiveCategory(e.target.value)}
                className="category-select"
                data-testid="category-select"
              >
                <option value="section">Section Zone</option>
                <option value="row">Row Line</option>
                <option value="seat">Seat Position</option>
                <option value="stage">Stage/Performance</option>
                <option value="equipment">Equipment</option>
                <option value="historical">Historical Note</option>
                <option value="custom">Custom</option>
              </select>
              
              {/* Seat Template Controls - shown when Place Seat is active */}
              {activeTool === TOOLS.PLACE_SEAT && (
                <div className="seat-placement-panel" data-testid="seat-placement-panel">
                  <div className="panel-divider" />
                  
                  {/* Template Selection */}
                  <SeatTemplateSelector 
                    templates={templates}
                    activeTemplate={activeTemplate}
                    onSelect={setActiveTemplate}
                  />
                  
                  {/* Seat Location Inputs */}
                  <div className="seat-location-inputs">
                    <div className="input-row">
                      <label>Section</label>
                      <input 
                        type="text"
                        value={pendingSeatSection}
                        onChange={(e) => setPendingSeatSection(e.target.value.toUpperCase())}
                        placeholder="BB"
                        data-testid="seat-section-input"
                      />
                    </div>
                    <div className="input-row">
                      <label>Row</label>
                      <input 
                        type="text"
                        value={pendingSeatRow}
                        onChange={(e) => setPendingSeatRow(e.target.value)}
                        placeholder="1"
                        data-testid="seat-row-input"
                      />
                    </div>
                    <div className="input-row">
                      <label>Seat #</label>
                      <input 
                        type="text"
                        value={pendingSeatNumber}
                        onChange={(e) => setPendingSeatNumber(e.target.value)}
                        placeholder="14"
                        data-testid="seat-number-input"
                      />
                    </div>
                  </div>
                  
                  {/* Rotation Control */}
                  <RotationControl 
                    rotation={activeSeatRotation}
                    onChange={setActiveSeatRotation}
                  />
                  
                  {/* Clone Controls */}
                  <CloneControls 
                    selectedSeat={selectedPlacedSeat}
                    onClone={async (sourceId, direction, newNumber, offset) => {
                      const result = await cloneSeat(sourceId, direction, newNumber, offset);
                      if (result.error) {
                        setSaveMessage(`Clone error: ${result.error}`);
                      } else {
                        setSaveMessage(`Cloned to seat ${newNumber}`);
                        setSelectedPlacedSeatId(result.seat?.id);
                      }
                      setTimeout(() => setSaveMessage(''), 2000);
                    }}
                  />
                  
                  {/* Row Generator Button */}
                  <div className="row-generator-button-wrapper" style={{ marginTop: 12, marginBottom: 8 }}>
                    <button
                      onClick={() => setShowRowGenerator(true)}
                      className="tool-btn"
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        backgroundColor: '#4F46E5',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: 12,
                        fontWeight: 500
                      }}
                      data-testid="open-row-generator-btn"
                    >
                      <span style={{ marginRight: 4 }}>⊞</span> Generate Row
                    </button>
                    <div style={{ fontSize: 10, color: '#6B7280', marginTop: 4 }}>
                      Place 2 control seats first, then generate a full row
                    </div>
                  </div>
                  
                  {/* Advanced Geometry Panel - Diagnostic Only */}
                  <AdvancedGeometryPanel
                    template={activeTemplate}
                    chartId={activeChartId}
                    currentCoords={cursorPosition}
                    onPlace={async (seatData) => {
                      const result = await placeSeat(seatData);
                      if (result.error) {
                        setSaveMessage(`Placement error: ${result.error}`);
                      } else {
                        setSaveMessage(`Placed ${seatData.section} Row ${seatData.row} Seat ${seatData.seat}`);
                        setSelectedPlacedSeatId(result.seat?.id);
                      }
                      setTimeout(() => setSaveMessage(''), 3000);
                      return result;
                    }}
                  />
                  
                  {/* Placed Seats List */}
                  <div className="placed-seats-list">
                    <div className="panel-header">
                      <span>Placed Seats ({placedSeats.length})</span>
                    </div>
                    {placedSeats.length === 0 ? (
                      <div className="empty-message">No seats placed yet</div>
                    ) : (
                      <div className="seats-grid">
                        {placedSeats.slice(0, 20).map(seat => (
                          <div 
                            key={seat.id}
                            className={`seat-chip ${selectedPlacedSeatId === seat.id ? 'seat-chip--selected' : ''}`}
                            onClick={() => setSelectedPlacedSeatId(seat.id)}
                          >
                            {seat.section} R{seat.row} S{seat.seat}
                          </div>
                        ))}
                        {placedSeats.length > 20 && (
                          <div className="more-seats">+{placedSeats.length - 20} more</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Undo/Redo */}
              <div className="tool-row">
                <button 
                  onClick={handleUndo} 
                  disabled={!canUndo}
                  className="tool-btn tool-btn--small"
                >
                  ↺ Undo {undoCount > 0 && `(${undoCount})`}
                </button>
                <button 
                  onClick={handleRedo} 
                  disabled={!canRedo}
                  className="tool-btn tool-btn--small"
                >
                  ↻ Redo {redoCount > 0 && `(${redoCount})`}
                </button>
              </div>
            </div>
            
            {/* Overlays List Panel */}
            <div className={`overlays-panel ${overlaysPanelCollapsed ? 'overlays-panel--collapsed' : ''}`}>
              <div className="panel-header" onClick={() => setOverlaysPanelCollapsed(!overlaysPanelCollapsed)}>
                <span>Overlays ({chartOverlays.length})</span>
                <button className="panel-toggle">{overlaysPanelCollapsed ? '+' : '−'}</button>
              </div>
              {!overlaysPanelCollapsed && (
                <div className="overlays-list-content">
                  {chartOverlays.length === 0 ? (
                    <div className="empty-message">No overlays on this chart</div>
                  ) : (
                    chartOverlays.map(overlay => (
                      <div 
                        key={overlay.id}
                        className={`overlay-item ${selectedOverlayId === overlay.id ? 'overlay-item--selected' : ''}`}
                        onClick={() => handleSelectOverlay(overlay.id)}
                      >
                        <span className="overlay-name">{overlay.name || 'Unnamed'}</span>
                        <span className={`overlay-status overlay-status--${overlay.publication_status || 'draft'}`}>
                          {overlay.publication_status || 'draft'}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            
            {/* Seat Inventory List Panel - persisted canonical seats only, with governed delete */}
            {inventorySeats.length > 0 && (
              <div className={`overlays-panel ${seatInventoryPanelCollapsed ? 'overlays-panel--collapsed' : ''}`} data-testid="seat-inventory-list-panel">
                <div className="panel-header" onClick={() => setSeatInventoryPanelCollapsed(!seatInventoryPanelCollapsed)}>
                  <span>Saved Seats ({inventorySeats.length})</span>
                  <button className="panel-toggle">{seatInventoryPanelCollapsed ? '+' : '−'}</button>
                </div>
                {!seatInventoryPanelCollapsed && (
                  <div className="overlays-list-content">
                    {inventorySeats.map(seat => (
                      <div
                        key={seat.id}
                        className={`overlay-item ${selectedPlacedSeatId === seat.id ? 'overlay-item--selected' : ''}`}
                        onClick={() => handleSelectInventorySeat(seat.id)}
                        data-testid={`seat-inventory-item-${seat.id}`}
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                      >
                        <span className="overlay-name">{seat.section} R{seat.row} S{seat.seat}</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span className={`overlay-status overlay-status--${seat.geometry_status || 'draft'}`}>
                            {seat.geometry_status || 'draft'}
                          </span>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteSeatGeometry(seat.id); }}
                            title="Delete this saved seat geometry"
                            data-testid={`delete-seat-inventory-item-${seat.id}`}
                            style={{ background: 'none', border: 'none', color: '#d44', cursor: 'pointer', fontSize: 13, padding: '2px 4px' }}
                          >
                            ×
                          </button>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Tool Help Panel */}
            <div className="tool-help-panel">
              {activeTool === TOOLS.SELECT && (
                <p><strong>Select</strong> — Click a seat draft to select it. Drag the body to move, drag corner handles to resize, use the top dot to rotate. Arrow keys nudge 1px (Shift = 5px). Delete removes it. Hold <strong>Space</strong> to pan.</p>
              )}
              {activeTool === TOOLS.HAND && (
                <p><strong>Hand</strong> — Click and drag to pan the chart. Scroll wheel to zoom. Nothing is drawn in this mode.</p>
              )}
              {activeTool === TOOLS.DRAW_SEAT && (
                <p><strong>Draw Seat Box</strong> — Click, hold and drag to draw a seat box at any size, then release. It stays an unsaved draft — label it in the panel and press Save. Hold <strong>Space</strong> to pan instead.</p>
              )}
              {activeTool === TOOLS.PLACE_SEAT && (
                <p><strong>Place Template Seat</strong> — Single-click to drop a fixed template-sized seat draft. Label it, then Save.</p>
              )}
              {activeTool === TOOLS.DRAW_RECT && (
                <p><strong>Rectangle</strong> — Click and drag to draw a zone.</p>
              )}
              {activeTool === TOOLS.DRAW_POLYGON && (
                <p><strong>Polygon</strong> — Click to add points. Double-click to complete. ESC to cancel.</p>
              )}
              {activeTool === TOOLS.PLACE_MARKER && (
                <p><strong>Marker</strong> — Click to place a point marker.</p>
              )}
            </div>
          </aside>
        ) : (
          /* Collapsed Rail Tab */
          <button 
            className="rail-expand-btn"
            onClick={() => setLeftPanelCollapsed(false)}
            title="Show panels"
          >
            ›
          </button>
        )}
        
        {/* Properties Panel (when overlay selected) */}
        {showPropertiesPanel && selectedOverlay && !selectedDraft && (
          <OverlayPropertiesPanel
            overlay={selectedOverlay}
            onUpdate={handleUpdateOverlay}
            onDelete={handleDeleteOverlay}
            onStatusChange={handleStatusChange}
            onClose={() => { setShowPropertiesPanel(false); setSelectedOverlayId(null); }}
            canEditGeometry={canEditGeometry(selectedOverlay)}
            canEditMetadata={canEditMetadata(selectedOverlay)}
          />
        )}
        
        {/* Seat Draft Properties Panel (draw-first labelling) */}
        {selectedDraft && (
          <DraftSeatPropertiesPanel
            draft={selectedDraft}
            chartLabel={activeChart.label}
            templateName={
              (templates.find(t => t.id === selectedDraft.template_id)?.name)
              || activeTemplate?.name || 'None'
            }
            onChange={handleChangeDraft}
            onSave={handleSaveOneDraft}
            onDuplicate={handleDuplicateDraft}
            onDuplicateDirection={handleDuplicateDirection}
            onSaveAsTemplate={handleSaveAsTemplate}
            onDelete={handleDeleteDraft}
            onClose={() => setSelectedDraftId(null)}
            saving={draftSaving}
          />
        )}
        
        {/* Persisted Seat Geometry Properties Panel (governed delete) */}
        {selectedInventorySeat && !selectedDraft && !(showPropertiesPanel && selectedOverlay) && (
          <SeatGeometryPropertiesPanel
            seat={selectedInventorySeat}
            chartLabel={activeChart.label}
            deleting={seatGeometryDeleting}
            deleteError={seatGeometryDeleteError}
            onDelete={handleDeleteSeatGeometry}
            onClose={() => { setSelectedPlacedSeatId(null); setSeatGeometryDeleteError(null); setSeatGeometryStatusError(null); }}
            onUpdateStatus={handleUpdateSeatGeometryStatus}
            statusUpdating={seatGeometryStatusUpdating}
            statusError={seatGeometryStatusError}
          />
        )}
        
        {/* Loading Indicator */}
        {(!isReady || isLoading) && (
          <div className="loading-overlay">
            {isLoading ? 'Loading overlays...' : 'Loading...'}
          </div>
        )}
        
        {/* Polygon Drawing Hint */}
        {activeTool === TOOLS.DRAW_POLYGON && (
          <div className="drawing-hint">
            Click to add points · Double-click to complete · ESC to cancel
          </div>
        )}
      </div>
      
      {/* Status Bar */}
      <footer className="map-editor-status">
        <span><strong>{activeTool.replace(/_/g, ' ')}</strong></span>
        <span className="status-divider">|</span>
        <span>X {Math.round(mouseImageCoords.x)} Y {Math.round(mouseImageCoords.y)}</span>
        <span className="status-divider">|</span>
        <span>{activeChart.label} · {currentZoom.toFixed(2)}×</span>
        <span className="status-divider">|</span>
        <span>{chartOverlays.length} overlay{chartOverlays.length !== 1 ? 's' : ''} · {publishedCount} published</span>
        <span className="status-divider">|</span>
        <span>Undo {undoCount} · Redo {redoCount}</span>
        {inventoryStats && (
          <>
            <span className="status-divider">|</span>
            <span className="seat-stats" data-testid="owner-inventory-stats-bar">
              <span data-testid="stats-total-seats" style={{ color: '#E8E0D5' }}>{inventoryStats.total_seats} total</span>
              <span style={{ color: 'rgba(232, 224, 213, 0.5)', margin: '0 6px' }}>·</span>
              <span data-testid="stats-unassigned" style={{ color: '#3B82F6' }}>{inventoryStats.unassigned_count} unassigned</span>
              <span style={{ color: 'rgba(232, 224, 213, 0.5)', margin: '0 6px' }}>·</span>
              <span data-testid="stats-pending" style={{ color: '#F97316' }}>{inventoryStats.assigned_pending_count} pending</span>
              <span style={{ color: 'rgba(232, 224, 213, 0.5)', margin: '0 6px' }}>·</span>
              <span data-testid="stats-verified" style={{ color: '#22C55E' }}>{inventoryStats.assigned_verified_count} verified</span>
              <span style={{ color: 'rgba(232, 224, 213, 0.5)', margin: '0 6px' }}>·</span>
              <span data-testid="stats-published" style={{ color: '#16A34A' }}>{inventoryStats.assigned_published_count} published</span>
              <span style={{ color: 'rgba(232, 224, 213, 0.5)', margin: '0 6px' }}>·</span>
              <span data-testid="stats-unavailable" style={{ color: '#9CA3AF' }}>{inventoryStats.unavailable_count} unavailable</span>
              {inventoryStats.conflict_count > 0 && (
                <>
                  <span style={{ color: 'rgba(232, 224, 213, 0.5)', margin: '0 6px' }}>·</span>
                  <span data-testid="stats-conflicts" style={{ color: '#DC2626', fontWeight: 700 }}>{inventoryStats.conflict_count} conflicts</span>
                </>
              )}
            </span>
          </>
        )}
      </footer>
      
      {/* Seat Assignment Modal */}
      <SeatAssignmentModal
        isOpen={showAssignmentModal}
        onClose={() => {
          setShowAssignmentModal(false);
          setAssignmentTargetSeat(null);
        }}
        seat={assignmentTargetSeat}
        section={assignmentTargetSeat?.section}
        unassignedTickets={unassignedTickets}
        onAssign={handleAssignTicket}
        onCreateNewTicket={() => {
          console.log('Create new ticket');
          // TODO: Open ticket creation modal
        }}
        isLoading={inventoryLoading}
      />
      
      {/* Owner Manage Assignment Modal - opens when clicking an assigned seat */}
      <AssignmentManagerModal
        isOpen={showAssignmentManager}
        seat={assignmentManagerSeat}
        chartLabel={activeChart.label}
        onClose={handleCloseAssignmentManager}
        getAssignmentDetail={getAssignmentDetail}
        onVerify={verifyAssignment}
        onReopen={reopenVerification}
        onPublish={publishAssignment}
        onUnpublish={unpublishAssignment}
        onRemove={async (assignmentId, reason) => {
          const ok = await removeInventoryAssignment(assignmentId, reason);
          return { ok: !!ok, error: ok ? undefined : (inventoryError || 'Failed to remove assignment') };
        }}
        onReassign={reassignTicket}
        getUnassignedTickets={getUnassignedTickets}
        onViewFullTicket={handleViewFullTicket}
      />
      
      {/* Row Generator Panel - shown in left rail when active */}
      {showRowGenerator && (
        <div className="row-generator-overlay" style={{
          position: 'absolute',
          top: 80,
          left: leftPanelCollapsed ? 10 : 430,
          zIndex: 100,
          width: 360
        }}>
          <RowGeneratorPanel
            isOpen={showRowGenerator}
            onClose={() => setShowRowGenerator(false)}
            chartId={activeChartId}
            section={pendingSeatSection}
            template={activeTemplate}
            controlSeat1={controlSeat1}
            controlSeat2={controlSeat2}
            onPreview={handlePreviewRow}
            onConfirm={handleConfirmRow}
            onCancel={handleCancelRowPreview}
            previewData={rowPreviewData}
            isLoading={inventoryLoading}
          />
        </div>
      )}
    </div>
  );
};

export default MapEditorStudio;
